import type { AppointmentResultReportCondition } from '@/api/types/ProviderApi'
import { colors } from '@/constants/colors'
import { Rise } from '@/ui/core/motion'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * One finding: its number on a white disc, the clinician's title, and their words in full
 * beneath it. Nothing is clamped and nothing opens — the card was expanded to read this, so
 * asking for a second tap to see any of it would be a poor trade.
 *
 * The geometry is the app's row: a 40pt disc, 14pt to the words, and the hairline starting where
 * they do rather than at the card's edge.
 */
const ResultCondition = ({
	condition,
	index,
	last,
}: {
	condition: AppointmentResultReportCondition
	index: number
	last: boolean
}) => (
	<Rise index={index}>
		<View className="relative">
			{!last && (
				<View
					className="absolute right-0 bottom-0 h-px"
					style={{ left: 54, backgroundColor: colors.divider }}
				/>
			)}
			<HStack gap={14} className="items-start py-4">
				<View className="h-10 w-10 items-center justify-center rounded-full bg-white">
					<Text className="font-display text-[17px] leading-[20px] tracking-[-0.02em]">
						{String(index + 1).padStart(2, '0')}
					</Text>
				</View>
				<VStack gap={5} className="flex-1 pt-1.5">
					<Text size="body0" weight="bold">
						{condition.title}
					</Text>
					<Text tone="soft">{condition.description}</Text>
				</VStack>
			</HStack>
		</View>
	</Rise>
)

/**
 * What the clinician found, in the order they wrote it. A numbered list rather than a set of
 * cards: the card holding it is the surface, so a report of six findings reads as one document
 * instead of six objects.
 */
const ResultConditionsList = ({
	conditions = [],
}: { conditions?: AppointmentResultReportCondition[] }) => (
	<View>
		{conditions.map((condition, index) => (
			<ResultCondition
				key={condition.title}
				condition={condition}
				index={index}
				last={index === conditions.length - 1}
			/>
		))}
	</View>
)

export { ResultConditionsList }
